/**
 * Settings deviations admin API module — the account-side view of every
 * location that has customized an account-default setting, plus the bulk
 * "Use account value" / "Set a value" actions and the impact preview.
 *
 * Endpoints (all under /accounts/{accountId}/settings):
 *   GET  /families                → the catalogue of families + fields (label, kind)
 *   GET  /deviations?…            → rows of (location, family, field) that differ
 *   POST /deviations/reset        → clear the location cells (follow the account)
 *   POST /deviations/set          → write one value into the location cells
 *   GET  /impact?family&field&value → who an account-level change would reach
 *
 * Location groups: /deviations and /impact take group_id= to scope rows and
 * totals to a group's members; reset / set accept { group_id } in place of
 * location_ids to act on the whole group.
 *
 * Vocabulary: a location either "follows the account" or has "customized"
 * a field. `account_value` on a deviation row is what the location would
 * have if it followed; secret kinds carry "set" / "unset" instead of the value.
 *
 * Families: edit_lock (accounting), work_order_checks / required_fields
 * (workorders), session_policy (worksessions) and location_defaults
 * (accounting; labor_rate / auto_approval_amount as decimal strings — kind
 * "string", printed as currency). Fields the account has LOCKED never appear
 * here; a bulk set/reset on a locked field returns per-location ok:false.
 */
import { webApiClient } from '../apiClient';
import { dedup } from './helpers';

export type SettingsFamilyName = 'edit_lock' | 'work_order_checks' | 'required_fields' | 'session_policy' | 'location_defaults';
/** The family's permission area (policies:<area>:manage). */
export type SettingsArea = 'workorders' | 'accounting' | 'worksessions';
/** Server `area` filter of the deviations feed — one per account tab. */
export type SettingsDeviationsArea = 'work-orders' | 'time-attendance';
export type SettingsFieldKind = 'bool' | 'int' | 'string' | 'string_list' | 'secret';

export interface SettingsFieldDef {
  key: string;
  label: string;
  kind: SettingsFieldKind;
}

export interface SettingsFamily {
  name: SettingsFamilyName;
  area: SettingsArea;
  label: string;
  fields: SettingsFieldDef[];
}

export interface SettingsFamiliesResponse {
  families: SettingsFamily[];
}

export interface SettingsDeviation {
  location_id: string;
  location_name: string;
  /** The location group the location belongs to, if any. */
  location_group_id?: string | null;
  location_group_name?: string | null;
  family: SettingsFamilyName;
  field: string;
  label: string;
  /** What the location would have if it followed the account. */
  account_value: unknown;
  /** The location's own value ("set" / "unset" for secret kinds). */
  location_value: unknown;
  updated_at?: string | null;
  updated_by?: string | null;
}

/** How many locations customize one setting (before the field filter and paging). */
export interface SettingsFieldCount {
  family: SettingsFamilyName;
  field: string;
  label: string;
  locations: number;
}

export interface SettingsDeviationsResponse {
  total_locations: number;
  following_all_count: number;
  customized_location_count: number;
  total: number;
  items: SettingsDeviation[];
  /** One entry per customized setting across the requested families (absent from older servers). */
  fields?: SettingsFieldCount[];
}

export interface SettingsDeviationsQuery {
  /** Server area filter (e.g. "work-orders"); omitted = every family. */
  area?: SettingsDeviationsArea | string;
  family?: SettingsFamilyName | string;
  field?: string;
  location_id?: string;
  /** Only members of this location group (items and the header totals). */
  group_id?: string;
  /** Only locations whose name contains this (case-insensitive). */
  q?: string;
  limit?: number;
  offset?: number;
}

export interface DeviationActionResult {
  location_id: string;
  ok: boolean;
  error?: string;
}

export interface DeviationActionResponse {
  results: DeviationActionResult[];
}

/**
 * Who a reset / set reaches: an explicit list of locations OR a whole location
 * group (every member, an empty group is a 200 no-op). Sending both is a 400.
 */
export type DeviationTarget = { location_ids: string[]; group_id?: never } | { group_id: string; location_ids?: never };

export type ResetDeviationsBody = {
  family: SettingsFamilyName | string;
  /** Omitted or "*" = every cell of that family. */
  field?: string;
} & DeviationTarget;

export type SetDeviationsBody = {
  family: SettingsFamilyName | string;
  field: string;
  value: unknown;
} & DeviationTarget;

export interface SettingsImpact {
  total_locations: number;
  following_count: number;
  custom_count: number;
  changes_count: number;
  current_value: unknown;
}

function errorMessage(res: { error?: string; errorBody?: any }, fallback: string): string {
  // The ApiClient flattens the error body: codes live on `errorBody`, not on
  // the thrown message.
  const eb: any = res.errorBody;
  if (eb) return eb.error || eb.message || res.error || fallback;
  return res.error || fallback;
}

const base = (accountId: string) => `/accounts/${accountId}/settings`;

export async function getSettingsFamilies(accountId: string): Promise<SettingsFamily[]> {
  return dedup(`getSettingsFamilies:${accountId}`, async () => {
    const res = await webApiClient.get<SettingsFamiliesResponse>(`${base(accountId)}/families`);
    if (!res.success) throw new Error(errorMessage(res, 'Failed to load the settings catalogue'));
    return res.data?.families ?? [];
  });
}

export async function getSettingsDeviations(accountId: string, query: SettingsDeviationsQuery = {}): Promise<SettingsDeviationsResponse> {
  const params = new URLSearchParams();
  if (query.area) params.set('area', query.area);
  if (query.family) params.set('family', query.family);
  if (query.field) params.set('field', query.field);
  if (query.location_id) params.set('location_id', query.location_id);
  if (query.group_id) params.set('group_id', query.group_id);
  if (query.q?.trim()) params.set('q', query.q.trim());
  params.set('limit', String(query.limit ?? 100));
  params.set('offset', String(query.offset ?? 0));
  const qs = params.toString();
  return dedup(`getSettingsDeviations:${accountId}:${qs}`, async () => {
    const res = await webApiClient.get<SettingsDeviationsResponse>(`${base(accountId)}/deviations?${qs}`);
    if (!res.success) throw new Error(errorMessage(res, 'Failed to load location settings'));
    const d = res.data;
    return {
      total_locations: d?.total_locations ?? 0,
      following_all_count: d?.following_all_count ?? 0,
      customized_location_count: d?.customized_location_count ?? 0,
      total: d?.total ?? 0,
      // nil slice → null from Go; normalise so callers can map.
      items: Array.isArray(d?.items) ? d.items : [],
      fields: Array.isArray(d?.fields) ? d.fields : [],
    };
  });
}

export async function resetSettingsDeviations(accountId: string, body: ResetDeviationsBody): Promise<DeviationActionResult[]> {
  const res = await webApiClient.post<DeviationActionResponse>(`${base(accountId)}/deviations/reset`, body);
  if (!res.success) throw new Error(errorMessage(res, 'Failed to reset the selected locations'));
  return Array.isArray(res.data?.results) ? res.data.results : [];
}

export async function setSettingsDeviations(accountId: string, body: SetDeviationsBody): Promise<DeviationActionResult[]> {
  const res = await webApiClient.post<DeviationActionResponse>(`${base(accountId)}/deviations/set`, body);
  if (!res.success) throw new Error(errorMessage(res, 'Failed to update the selected locations'));
  return Array.isArray(res.data?.results) ? res.data.results : [];
}

export async function getSettingsImpact(
  accountId: string,
  query: { family: SettingsFamilyName | string; field: string; value: unknown; group_id?: string }
): Promise<SettingsImpact> {
  const params = new URLSearchParams();
  params.set('family', query.family);
  params.set('field', query.field);
  params.set('value', JSON.stringify(query.value));
  if (query.group_id) params.set('group_id', query.group_id);
  const res = await webApiClient.get<SettingsImpact>(`${base(accountId)}/impact?${params.toString()}`);
  if (!res.success) throw new Error(errorMessage(res, 'Failed to preview the change'));
  return res.data as SettingsImpact;
}

/** How many settings of each topic a location has customized away from the account. */
export interface LocationCustomCounts {
  /** Work order checks + edit lock cells set at the location. */
  work_orders: number;
  /** Work session policy cells set at the location (the geofence centre is not a customization). */
  time_attendance: number;
}

/**
 * The "N custom" badges of the Location dialog's tabs, in one request — they
 * used to read three whole policy envelopes per open just to count cells.
 * The API takes the account from the location; `accountId` only scopes the
 * de-duplication key, as the cache key does.
 */
export async function getLocationCustomCounts(accountId: string, locationId: string): Promise<LocationCustomCounts> {
  return dedup(`getLocationCustomCounts:${accountId}:${locationId}`, async () => {
    const res = await webApiClient.get<LocationCustomCounts>(`/locations/${locationId}/custom-setting-counts`);
    if (!res.success) throw new Error(errorMessage(res, 'Failed to load customized settings'));
    return { work_orders: res.data?.work_orders ?? 0, time_attendance: res.data?.time_attendance ?? 0 };
  });
}

export const policyDeviationsAdmin = {
  getLocationCustomCounts,
  getSettingsFamilies,
  getSettingsDeviations,
  resetSettingsDeviations,
  setSettingsDeviations,
  getSettingsImpact,
};
